import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import {FormControl} from '@angular/forms';
import {BehaviorSubject, Observable} from 'rxjs';
import {map, startWith, tap} from 'rxjs/operators';
import {sortNumbersAtEnd} from '../../../../classes/collectionUtils';
import {IPromptDefaults} from '../../../../constants/defaults';

@Component({
    selector: 'app-options-autocomplete',
    templateUrl: './options-autocomplete.component.html',
    styleUrl: './options-autocomplete.component.scss',
    standalone: false
})
export class OptionsAutocompleteComponent implements OnInit {
  @Input() chosenOptions!: string[]; // TODO: Will need to hold FAVOURITE options
  @Output() chosenOptionsChange = new EventEmitter<string[]>();

  @Input() title!: string;
  @Input() icon!: string;
  @Input() suggestedOptions!: IPromptDefaults[];
  @Input() placeholder!: string;

  optionControl = new FormControl();
  filteredOptions!: Observable<string[]>;
  addingOption = false;
  preventTabFocusOnFirstLoad = -1;
  searchInputFocused = false;
  private suggestedOptionsSubject!: BehaviorSubject<IPromptDefaults[]>;
  suggestedOptions$!: Observable<IPromptDefaults[]>;

  @ViewChild('input') userInput: ElementRef | undefined;

  ngOnInit(): void {
    this.suggestedOptionsSubject = new BehaviorSubject<IPromptDefaults[]>(this.suggestedOptions);
    this.suggestedOptions$ = this.suggestedOptionsSubject.asObservable();
    this.buildPlaceholder();

    this.filteredOptions = this.optionControl.valueChanges.pipe(
      startWith(''),
      tap(() => this.updateChosenOptions()),
      map(value => this._filter(value))
    );

    this.suggestedOptions$ = this.optionControl.valueChanges.pipe(
      startWith(''),
      tap(() => this.updateChosenOptions()),
      map(value => this._filterSuggestedOptions(value))
    );

    setTimeout(() => { this.markChosenOptionsAsAdded(); });
    setTimeout(() => { this.preventTabFocusOnFirstLoad = 0; }, 500);
  }

  protected removeItem(item: string, event: Event) {
    event.stopPropagation();
    this.chosenOptions = this.chosenOptions.filter(i => i !== item);

    const suggestedOptions = this.suggestedOptionsSubject.value;
    suggestedOptions.forEach(suggestedOption => {
      if (suggestedOption.option === item) {
        suggestedOption.added = false;
      }
    });
    this.suggestedOptionsSubject.next(suggestedOptions);

    this.optionControl.setValue(null);

    this.buildPlaceholder();
  }

  protected stayOpen(item: string, event?: MouseEvent) { // TODO: Favourite current and ping it to the top
    event?.stopPropagation();
  }

  protected addSuggestionToList(item: string, event: MouseEvent, i: number) {
    event?.stopPropagation();

    if (!this.chosenOptions.includes(item))
      this.chosenOptions.push(item);
    const suggestedOptions = this.suggestedOptionsSubject.value;
    suggestedOptions[i].added = true;
    this.suggestedOptionsSubject.next(suggestedOptions);

    this.optionControl.setValue(null); // TODO: Better to update this.filteredItems directly?

    this.buildPlaceholder();
  }

  protected clearInput(inputElement: HTMLElement, focusInput: boolean): void {
    this.optionControl.setValue(null);
    if (focusInput)
      inputElement.focus();
  }

  protected addNewUserOption() {
    this.addingOption = true;
    setTimeout(() => { this.addingOption = false; }, 500);
      const newOption = this.optionControl.value;
      if (newOption && !this.chosenOptions.includes(newOption)) {
        this.chosenOptions.push(newOption);
      }
      this.optionControl.setValue('');
      this.userInput?.nativeElement.focus();
    this.buildPlaceholder();
  }

  private updateChosenOptions() {
    this.chosenOptionsChange.emit(this.chosenOptions);
  }

  private _filter(value: string | null): string[] {
    if (!value) {
      return sortNumbersAtEnd(this.chosenOptions.sort((a, b) => a.localeCompare(b)));
    }
    const filterValue = value.toLowerCase();

    return sortNumbersAtEnd(this.chosenOptions
      .filter(item => item.toLowerCase().includes(filterValue))
      .sort((a, b) => a.localeCompare(b)));
  }

  private _filterSuggestedOptions(value: string): IPromptDefaults[] {
    if (!value)
      return this.suggestedOptionsSubject.value;

    const filterValue = value.toLowerCase();
    return this.suggestedOptionsSubject.value.filter(suggestedOption =>
      suggestedOption.option.toLowerCase().includes(filterValue) && !suggestedOption.added
    );
  }

  private buildPlaceholder(): void {
    if (this.chosenOptions.length === 0)
      this.placeholder = 'Search or add your own...';
    else if (this.chosenOptions.length === 1)
      this.placeholder = this.chosenOptions[0];
    else if (this.chosenOptions.length > 1) {
      const formattedArray = this.chosenOptions.slice(0, -1).join(', ');
      const lastElement = this.chosenOptions[this.chosenOptions.length - 1];

      this.placeholder = `${formattedArray} and ${lastElement}`;
    }
  }

  private markChosenOptionsAsAdded(): void {
    const suggestedOptions = this.suggestedOptionsSubject.value;
    this.chosenOptions.forEach(chosenOption => {
      const optionToUpdate = suggestedOptions.find(suggestedOption => suggestedOption.option === chosenOption);
      if (optionToUpdate)
        optionToUpdate.added = true;
    });
    this.suggestedOptionsSubject.next(suggestedOptions);
  }

}
